import React from 'react'

import { Box } from '@mui/material'
import CustomerCodeStatus from 'features/customers/components/CustomerCodeStatus'
import CustomerContactsLimit from 'features/customers/components/CustomerContactsLimit'
import CustomerLeadsLimit from 'features/customers/components/CustomerLeadsLimit'
import CustomersFilters from 'features/customers/components/CustomersFilters'
import PlanBadge from 'features/customers/components/PlanBadge'
import { CUSTOMERS_INITIAL_FILTERS } from 'features/customers/constants/fliters'
import { subscriptionStatus } from 'features/customers/constants/subscriptionStatus'
import { useCustomers } from 'features/customers/hooks/useCustomers'
import { UrlProvider } from 'urls'

import { Badge, Flag, StatusChip } from 'components/_base'
import { Table } from 'components/table'
import { usePaginatedTableMeta } from 'hooks/usePaginatedTableMeta'
import { formatDate } from 'utils/date'

const config = [
  {
    title: 'Customer ID',
    headerStyle: { width: 95 },
    field: 'id',
  },
  {
    title: 'Name',
    noBreak: true,
    render: ({ deleted_at, name }) => (
      <>
        {name && (
          <Box component="span" sx={{ mr: '1rem' }}>
            {name}
          </Box>
        )}
        {deleted_at && <Badge color="red" text="Deleted" />}
      </>
    ),
    sort: 'name',
  },
  {
    title: 'Website',
    field: 'website',
    noBreak: true,
  },
  {
    title: 'Country',
    headerStyle: { width: 70 },
    render: ({ country }) => (country ? <Flag country={country} /> : null),
  },
  {
    title: 'Client ID',
    headerStyle: { width: 100 },
    field: 'client_id',
  },
  {
    title: 'Code status',
    headerStyle: { width: 130 },
    render: CustomerCodeStatus,
  },
  {
    title: 'Plan',
    headerStyle: { width: 160 },
    render: ({ plan }) => <PlanBadge name={plan?.name} />,
  },
  {
    title: 'Stripe Subscription',
    headerStyle: { width: 155 },
    render: ({ stripe_subscription_status }) => <StatusChip {...subscriptionStatus(stripe_subscription_status)} />,
  },
  {
    title: 'Intent Companies limit',
    render: ({ id }) => <CustomerLeadsLimit customerId={id} />,
  },
  {
    title: 'Contact limit',
    headerStyle: { width: 110 },
    render: ({ id }) => <CustomerContactsLimit customerId={id} />,
  },
  {
    title: 'Registered',
    headerStyle: { width: 145 },
    render: ({ created_at }) => formatDate(created_at),
    sort: 'created_at',
  },
]

const Customers = () => {
  const { meta, setPage, setRowsPerPage, setSort, setFilters } = usePaginatedTableMeta(
    {
      filters: CUSTOMERS_INITIAL_FILTERS,
    },
    'customers',
  )
  const { data, isLoading, error } = useCustomers(meta)

  const handleSort = property => () => setSort(property)
  const getRowLink = customer => UrlProvider.getUrl('customer', { id: customer.id, tab: '' })

  return (
    <Table
      config={config}
      count={data?.count}
      data={data?.items}
      error={error ? 'Error occurred when loading customers data' : undefined}
      getRowLink={getRowLink}
      hover
      loading={isLoading}
      minWidth={1400}
      onChangePage={setPage}
      onChangeRowsPerPage={setRowsPerPage}
      onSort={handleSort}
      page={meta.page}
      rowsPerPage={meta.rowsPerPage}
      sortBy={meta.sortBy}
      sortOrder={meta.sortOrder}
      title="Customers"
      toolbar={<CustomersFilters initialValues={meta.filters} setFilters={setFilters} />}
    />
  )
}

export default Customers
